import React, { useState, useRef, useEffect } from 'react';
import { brandColors } from '../styles/clientManagementStyles';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import { formatDateOr } from '../utils/safeDate';

// No time at all rather than "Invalid Date" when a message has none
const fmtTime = (iso) => formatDateOr(iso, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}, '', 'toLocaleString');

const ClientMessagesTab = ({ selectedClient, messages, loadingMessages, sendingMessage, onSend, onRefresh }) => {
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    // Cleared only once it has gone: a failed send keeps what the coach wrote
    if (await onSend(text)) setDraft('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '520px', backgroundColor: brandColors.darkGray, borderRadius: '8px', border: `1px solid rgba(255,255,255,0.06)`, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${brandColors.lightGray}`, flexShrink: 0 }}>
        <span style={{ color: brandColors.white, fontSize: '13px', fontWeight: 600 }}>
          Messages with {selectedClient?.full_name || 'client'}
        </span>
        <button
          onClick={onRefresh}
          disabled={loadingMessages}
          style={{ background: 'none', border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px', padding: '4px 10px', color: brandColors.silver, fontSize: '11px', cursor: loadingMessages ? 'not-allowed' : 'pointer' }}
        >
          {loadingMessages ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* Thread */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {messages.length === 0 ? (
          <div style={{ margin: 'auto', color: brandColors.silver, fontSize: '13px', textAlign: 'center' }}>
            No messages yet — send the first one below.
          </div>
        ) : (
          // A message with no text has nothing to show: no empty bubble
          messages.filter(msg => (msg.message || '').trim()).map(msg => {
            const isCoach = msg.sender_role === 'coach';
            return (
              <div key={msg.id} style={{ display: 'flex', justifyContent: isCoach ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '70%' }}>
                  <div style={{
                    padding: '9px 13px',
                    borderRadius: isCoach ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    backgroundColor: isCoach ? brandColors.gold : brandColors.mediumGray,
                    color: isCoach ? brandColors.darkGray : brandColors.white,
                    fontSize: '13px',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                  }}>
                    {msg.message}
                  </div>
                  <div style={{
                    fontSize: '11px', color: brandColors.silver, marginTop: '3px',
                    textAlign: isCoach ? 'right' : 'left',
                  }}>
                    {fmtTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px', borderTop: `1px solid ${brandColors.lightGray}`, flexShrink: 0 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type a message…"
          disabled={sendingMessage}
          style={{
            flex: 1, padding: '10px 12px', backgroundColor: brandColors.mediumGray,
            border: `1px solid ${brandColors.lightGray}`, borderRadius: '8px',
            color: brandColors.white, fontSize: '13px', outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={sendingMessage || !draft.trim()}
          style={{
            padding: '10px 18px', borderRadius: '8px', border: 'none',
            backgroundColor: brandColors.gold, color: brandColors.darkGray,
            fontSize: '13px', fontWeight: 700,
            cursor: sendingMessage || !draft.trim() ? 'not-allowed' : 'pointer',
            opacity: sendingMessage || !draft.trim() ? 0.6 : 1,
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default React.memo(ClientMessagesTab, shallowEqualIgnoringFunctions);
